import { Icon } from './Icon.tsx';
import type { Question } from '../features/surveys/api.ts';

// Field-optimised answer inputs for door-to-door collection on a phone:
// 16px type (prevents iOS focus-zoom), 48px+ tap targets, and choice/multi
// options rendered as full-width tappable rows that fill teal when ticked —
// like marking a box on a paper register.
const BIG_FIELD =
  'w-full rounded-xl border border-input bg-background px-4 py-3 text-base ' +
  'transition-colors outline-none focus:border-ring focus:ring-2 focus:ring-ring/30 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const OPTION_ROW =
  'flex min-h-[3.25rem] cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 ' +
  'text-base transition-colors select-none ' +
  'peer-focus-visible:ring-2 peer-focus-visible:ring-ring';

export function SurveyAnswerInput({
  question,
  value,
  onChange,
  disabled,
}: {
  question: Question;
  value: unknown;
  onChange: (v: unknown) => void;
  disabled?: boolean;
}): JSX.Element {
  const opts = question.options ?? [];

  switch (question.type) {
    case 'NUMBER':
      return (
        <input
          type="number"
          inputMode="decimal"
          className={BIG_FIELD}
          dir="ltr"
          disabled={disabled}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
    case 'DATE':
      return (
        <input
          type="date"
          className={BIG_FIELD}
          dir="ltr"
          disabled={disabled}
          value={typeof value === 'string' ? value.slice(0, 10) : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'SELECT':
      return (
        <select
          className={BIG_FIELD}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose one…</option>
          {opts.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      );
    case 'CHOICE':
      return (
        <fieldset className="space-y-2.5" disabled={disabled}>
          {opts.map((o) => {
            const selected = value === o;
            return (
              <label
                key={o}
                className={`${OPTION_ROW} ${
                  selected
                    ? 'border-primary bg-primary/[0.06] font-medium text-ink'
                    : 'border-border hover:bg-muted/60'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <input
                  type="radio"
                  name={question.id}
                  className="peer sr-only"
                  disabled={disabled}
                  checked={selected}
                  onChange={() => onChange(o)}
                />
                <span
                  aria-hidden
                  className={`flex h-5 w-5 flex-none items-center justify-center rounded-full border-2 transition-colors ${
                    selected ? 'border-primary' : 'border-border-strong'
                  }`}
                >
                  <span className={`h-2.5 w-2.5 rounded-full bg-primary transition-opacity ${selected ? 'opacity-100' : 'opacity-0'}`} />
                </span>
                <span>{o}</span>
              </label>
            );
          })}
        </fieldset>
      );
    case 'MULTISELECT': {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      const toggle = (o: string): void => {
        onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
      };
      return (
        <fieldset className="space-y-2.5" disabled={disabled}>
          {opts.map((o) => {
            const selected = arr.includes(o);
            return (
              <label
                key={o}
                className={`${OPTION_ROW} ${
                  selected
                    ? 'border-primary bg-primary/[0.06] font-medium text-ink'
                    : 'border-border hover:bg-muted/60'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <input
                  type="checkbox"
                  className="peer sr-only"
                  disabled={disabled}
                  checked={selected}
                  onChange={() => toggle(o)}
                />
                <span
                  aria-hidden
                  className={`flex h-5 w-5 flex-none items-center justify-center rounded-md border-2 transition-colors ${
                    selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border-strong'
                  }`}
                >
                  <Icon name="check" size={14} className={`transition-opacity ${selected ? 'opacity-100' : 'opacity-0'}`} />
                </span>
                <span>{o}</span>
              </label>
            );
          })}
        </fieldset>
      );
    }
    case 'COUNT_GROUP': {
      const obj = (value && typeof value === 'object' && !Array.isArray(value)) ? (value as Record<string, number>) : {};
      const total = opts.reduce((s, o) => s + (typeof obj[o] === 'number' ? obj[o] : 0), 0);
      const setCat = (cat: string, raw: string): void => {
        const next = { ...obj };
        if (raw === '') delete next[cat];
        else next[cat] = Number(raw);
        onChange(next);
      };
      return (
        <fieldset className="space-y-2.5" disabled={disabled}>
          {opts.map((cat) => (
            <label key={cat} className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5">
              <span className="text-base">{cat}</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                className="w-24 rounded-lg border border-input bg-background px-3 py-2 text-base text-right tabular outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
                value={obj[cat] === undefined ? '' : String(obj[cat])}
                onChange={(e) => setCat(cat, e.target.value)}
              />
            </label>
          ))}
          <div className="flex items-center justify-between rounded-xl bg-primary/[0.06] px-4 py-2.5">
            <span className="status-label !text-foreground">Total</span>
            <span className="text-lg font-bold text-primary tabular">{total}</span>
          </div>
        </fieldset>
      );
    }
    case 'PRIORITY': {
      // Full ranking via up/down arrows. Falls back to the admin's item order
      // until SurveyCollect seeds the answer; emits the reordered array.
      const order = Array.isArray(value) && value.length === opts.length && opts.every((o) => (value as string[]).includes(o))
        ? (value as string[])
        : opts;
      const move = (i: number, dir: -1 | 1): void => {
        const j = i + dir;
        if (j < 0 || j >= order.length) return;
        const next = [...order];
        [next[i], next[j]] = [next[j]!, next[i]!];
        onChange(next);
      };
      return (
        <fieldset className="space-y-2.5" disabled={disabled}>
          {order.map((item, i) => (
            <div key={item} className="flex min-h-[3.25rem] items-center gap-3 rounded-xl border border-border px-3 py-2.5">
              <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-primary/[0.08] text-sm font-bold text-primary tabular">{i + 1}</span>
              <span className="flex-1 text-base">{item}</span>
              <div className="flex flex-none items-center gap-1">
                <button
                  type="button"
                  disabled={disabled || i === 0}
                  onClick={() => move(i, -1)}
                  className="rounded-lg border border-border px-2 py-1.5 transition-colors hover:bg-muted/60 disabled:opacity-30"
                  aria-label={`Move ${item} up`}
                >
                  <Icon name="expand_less" size={18} />
                </button>
                <button
                  type="button"
                  disabled={disabled || i === order.length - 1}
                  onClick={() => move(i, 1)}
                  className="rounded-lg border border-border px-2 py-1.5 transition-colors hover:bg-muted/60 disabled:opacity-30"
                  aria-label={`Move ${item} down`}
                >
                  <Icon name="expand_more" size={18} />
                </button>
              </div>
            </div>
          ))}
        </fieldset>
      );
    }
    case 'TEXT':
    default:
      return (
        <input
          type="text"
          className={BIG_FIELD}
          dir="auto"
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
